import {
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  AlertCircle,
  ChevronLeft,
  DollarSign,
  ExternalLink,
  PiggyBank,
} from "lucide-react-native";
import { SectionTitleRow } from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import type {
  CostPaymentRecordParam,
  CostsStackParamList,
  MainTabParamList,
} from "../../types";
import { useLocale, useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import ThiingsIcon from "../../../shared/ThiingsIcon";
import type { ThiingsIconName } from "../../../shared/icons";
import { useSmartBack } from "../../hooks/useSmartBack";
import { type InfoSection } from "../../components/InfoSheet";
import SpendingAccountCard, {
  type SpendingAccount,
} from "../../components/SpendingAccountCard";
import PaymentEntryTypePicker from "../../components/PaymentEntryTypePicker";
import {
  CostDocumentUploadModal,
  type ManualPaymentEntry,
  type UploadedCostDocument,
} from "./suggested/CostsOverviewConceptScreen";
import {
  useCreatePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
} from "@src/packages/costs/hooks/usePaymentRecords";
import {
  useSaveTaxDeductionProfile,
  useSaveTaxDeductionReview,
  useDeleteTaxDeductionReview,
  useTaxDeductionCalculation,
  useTaxDeductionProfile,
  useTaxDeductionReviews,
} from "@src/packages/costs/hooks/useTaxDeductions";
import {
  MEDICAL_DEDUCTION_TAX_YEAR,
  calculateMedicalExpenseDeduction,
} from "@src/packages/costs/medicalExpenseDeduction";
import {
  buildTaxReviewSummary,
  type TaxReviewEntry,
} from "@src/packages/costs/taxReview";
import {
  attachDocumentToPaymentRecord,
  buildManualPaymentRecordPayload,
  buildUploadedPaymentRecordPayload,
  findPaymentRecordForUpload,
  itemizationChoice,
  itemizationValue,
  type ItemizationChoice,
} from "./taxReviewHelpers";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "./paymentRecordMapping";
import PaymentRecordDetailModal from "./PaymentRecordDetailModal";
import { getDevTaxReviewFixture } from "./taxReviewFixtures";
import TaxReviewSections from "./TaxReviewSections";
import {
  claimEffectiveLabel,
  isFinancialClaimCurrent,
  TAX_BENEFITS,
  type FinancialClaim,
  type TaxBenefit,
} from "@src/packages/costs/financialContent";

type Props = NativeStackScreenProps<CostsStackParamList, "TaxDeductions">;

function buildTaxSourceSections(benefit: TaxBenefit): InfoSection[] {
  return [
    {
      title: benefit.summary.label,
      body: [
        benefit.summary.value,
        benefit.summary.caveat,
        `${benefit.summary.provenance.jurisdiction} · ${claimEffectiveLabel(benefit.summary)} · Verified ${benefit.summary.provenance.lastVerifiedOn}`,
        `Source: ${benefit.summary.provenance.sourceUrl}`,
      ]
        .filter(Boolean)
        .join("\n\n"),
    },
    ...benefit.eligible
      .filter((claim) => claim.kind !== "account-rule")
      .map((claim) => ({
        title: claim.label,
        body: [
          claim.value,
          claim.caveat,
          `${claim.provenance.jurisdiction} · ${claimEffectiveLabel(claim)} · Verified ${claim.provenance.lastVerifiedOn}`,
          `Source: ${claim.provenance.sourceUrl}`,
        ]
          .filter(Boolean)
          .join("\n\n"),
      })),
  ];
}

function SourceLink({ claim }: { claim: FinancialClaim }) {
  const capturePress = usePressCapture("SourceLink");
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`Open IRS source for ${claim.label}`}
      onPress={() => {
        capturePress(pressLabel("IRS source"));
        Linking.openURL(claim.provenance.sourceUrl);
      }}
      style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
    >
      <Text
        className="font-dm-sans"
        style={{ fontSize: 11, color: EMBER.colors.warm, fontWeight: "600" }}
      >
        IRS source
      </Text>
      <ExternalLink size={12} color={EMBER.colors.warm} strokeWidth={1.7} />
    </Pressable>
  );
}

function ClaimRow({ claim }: { claim: FinancialClaim }) {
  return (
    <View
      style={{
        paddingTop: 10,
        paddingBottom: 10,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 11,
            color: EMBER.colors.textFaint,
            textTransform: "uppercase",
            letterSpacing: 0.35,
          }}
        >
          {claim.label}
        </Text>
        <SourceLink claim={claim} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.text,
          lineHeight: 19,
          marginTop: 3,
        }}
      >
        {claim.value}
      </Text>
      {claim.caveat && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textMid,
            lineHeight: 17,
            marginTop: 3,
          }}
        >
          {claim.caveat}
        </Text>
      )}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          color: EMBER.colors.textFaint,
          marginTop: 4,
        }}
      >
        {claim.provenance.jurisdiction} · {claimEffectiveLabel(claim)} ·
        Verified {claim.provenance.lastVerifiedOn}
      </Text>
    </View>
  );
}

function TaxBenefitCard({ benefit }: { benefit: TaxBenefit }) {
  const summaryCurrent = isFinancialClaimCurrent(benefit.summary);
  const currentEligible = benefit.eligible.filter((claim) =>
    isFinancialClaimCurrent(claim),
  );
  const benefitCurrent =
    summaryCurrent && currentEligible.length === benefit.eligible.length;

  return (
    <View style={{ ...(GLASS_CARD_STYLE as object), padding: 20 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <ThiingsIcon name={benefit.icon as ThiingsIconName} size={28} />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {benefit.title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {benefit.subtitle}
          </Text>
        </View>
      </View>

      {benefitCurrent ? (
        <>
          <ClaimRow claim={benefit.summary} />

          <View
            style={{
              backgroundColor: "rgba(255,255,255,0.3)",
              borderRadius: 10,
              padding: 14,
              gap: 0,
              marginTop: 12,
              marginBottom: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: EMBER.colors.textMid,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                marginBottom: 2,
              }}
            >
              What qualifies
            </Text>
            {currentEligible.map((claim) => (
              <View key={claim.id}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                    paddingTop: 8,
                  }}
                >
                  <DollarSign
                    size={13}
                    color={EMBER.colors.textMid}
                    strokeWidth={1.5}
                    style={{ marginTop: 2 }}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: EMBER.colors.text,
                      lineHeight: 18,
                    }}
                  >
                    <Text style={{ fontWeight: "600" }}>{claim.label}: </Text>
                    {claim.value}
                  </Text>
                </View>
                {claim.caveat && (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textMid,
                      lineHeight: 17,
                      marginLeft: 21,
                      marginTop: 2,
                    }}
                  >
                    {claim.caveat}
                  </Text>
                )}
                <View style={{ marginLeft: 21, marginTop: 3 }}>
                  <SourceLink claim={claim} />
                </View>
              </View>
            ))}
          </View>

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textMid,
              lineHeight: 17,
              fontStyle: "italic",
            }}
          >
            {benefit.note}
          </Text>
        </>
      ) : (
        <View style={{ paddingVertical: 10 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textMid,
              lineHeight: 19,
              marginBottom: 6,
            }}
          >
            Current details are unavailable until this guidance is reviewed.
          </Text>
          <SourceLink claim={benefit.summary} />
        </View>
      )}
    </View>
  );
}

export default function TaxDeductionsScreen({ navigation, route }: Props) {
  const region = useRegion();
  const { locale } = useLocale();
  const t = useTranslations();
  const capturePress = usePressCapture("TaxDeductionsScreen");
  const taxYear = MEDICAL_DEDUCTION_TAX_YEAR;
  const paymentRecordsQuery = usePaymentRecords();
  const taxProfileQuery = useTaxDeductionProfile(taxYear);
  const taxReviewsQuery = useTaxDeductionReviews(taxYear);
  const taxCalculationQuery = useTaxDeductionCalculation(taxYear);
  const saveTaxProfile = useSaveTaxDeductionProfile(taxYear);
  const saveTaxReview = useSaveTaxDeductionReview(taxYear);
  const deleteTaxReview = useDeleteTaxDeductionReview(taxYear);
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const [addTypePickerVisible, setAddTypePickerVisible] = useState(false);
  const [selectedAddKind, setSelectedAddKind] = useState<
    ManualPaymentEntry["kind"] | null
  >(null);
  const [selectedPaymentRecord, setSelectedPaymentRecord] =
    useState<CostPaymentRecordParam | null>(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [taxReviewAgiDraft, setTaxReviewAgiDraft] = useState<number | null>(
    null,
  );
  const [taxReviewItemizationDraft, setTaxReviewItemizationDraft] =
    useState<ItemizationChoice>("unknown");
  const [taxReviewInlineInputError, setTaxReviewInlineInputError] = useState<
    string | null
  >(null);
  const taxReviewInlineSaveTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const devTaxReviewFixture = useMemo(
    () => getDevTaxReviewFixture(taxYear),
    [taxYear],
  );
  const taxReviewSummary = useMemo(
    () =>
      buildTaxReviewSummary(
        devTaxReviewFixture?.records ?? paymentRecordsQuery.data ?? [],
        devTaxReviewFixture?.reviews ?? taxReviewsQuery.data ?? [],
        devTaxReviewFixture?.profile ?? taxProfileQuery.data ?? null,
        taxYear,
      ),
    [
      devTaxReviewFixture,
      paymentRecordsQuery.data,
      taxProfileQuery.data,
      taxReviewsQuery.data,
      taxYear,
    ],
  );
  const medicalExpenseBenefit = TAX_BENEFITS.find(
    (benefit) => benefit.id === "medical-expense-deduction",
  );
  const taxReviewInfoSections = medicalExpenseBenefit
    ? buildTaxSourceSections(medicalExpenseBenefit)
    : [];
  const taxReviewCalculation = useMemo(
    () =>
      calculateMedicalExpenseDeduction(
        taxReviewSummary.entries.map((entry) => entry.candidate),
        taxReviewAgiDraft,
        itemizationValue(taxReviewItemizationDraft),
      ),
    [taxReviewAgiDraft, taxReviewItemizationDraft, taxReviewSummary.entries],
  );
  useEffect(() => {
    const profile = devTaxReviewFixture?.profile ?? taxProfileQuery.data;
    setTaxReviewAgiDraft(profile?.adjustedGrossIncome ?? null);
    setTaxReviewItemizationDraft(
      itemizationChoice(profile?.itemizesDeductions),
    );
    setTaxReviewInlineInputError(null);
  }, [devTaxReviewFixture?.profile, taxProfileQuery.data]);
  useEffect(
    () => () => {
      if (taxReviewInlineSaveTimeoutRef.current) {
        clearTimeout(taxReviewInlineSaveTimeoutRef.current);
      }
    },
    [],
  );
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });
  const openSpendingAccountChat = (account: SpendingAccount) => {
    rootNavigation?.navigate("Chat", {
      screen: "Assistant",
      params: {
        initialPrompt: `I want to use my ${account} dollars for fertility care. Help me figure out what I can submit or reimburse. First check whether I have already uploaded an itemized bill, receipt, explanation of benefits, or plan document. Ask me for photos or uploads for anything missing, then help me identify what to confirm with my benefits administrator before I pay or submit it.`,
        autoSendInitialPrompt: true,
        returnTab: "Costs",
      },
    });
  };
  const scheduleTaxReviewInputs = (
    adjustedGrossIncome: number | null,
    itemizesDeductions: boolean | null,
  ) => {
    if (taxReviewInlineSaveTimeoutRef.current) {
      clearTimeout(taxReviewInlineSaveTimeoutRef.current);
    }

    if (adjustedGrossIncome === null) {
      setTaxReviewInlineInputError(t("costs.tax_review.invalid_agi"));
      return;
    }

    setTaxReviewInlineInputError(null);
    if (
      taxProfileQuery.data?.adjustedGrossIncome === adjustedGrossIncome &&
      taxProfileQuery.data.itemizesDeductions === itemizesDeductions
    ) {
      return;
    }

    taxReviewInlineSaveTimeoutRef.current = setTimeout(() => {
      void saveTaxProfile
        .mutateAsync({
          taxYear,
          adjustedGrossIncome,
          itemizesDeductions,
        })
        .catch((error: unknown) => {
          setTaxReviewInlineInputError(
            error instanceof Error
              ? error.message
              : t("costs.tax_review.save_tax_inputs_error"),
          );
        });
    }, 350);
  };

  const handleTaxReviewAgiChange = (value: number | null) => {
    setTaxReviewAgiDraft(value);
    scheduleTaxReviewInputs(value, itemizationValue(taxReviewItemizationDraft));
  };

  const handleTaxReviewItemizationChange = (value: ItemizationChoice) => {
    setTaxReviewItemizationDraft(value);
    scheduleTaxReviewInputs(taxReviewAgiDraft, itemizationValue(value));
  };

  const openPaymentDetails = (entry: TaxReviewEntry) => {
    setSelectedPaymentRecord(apiToUiPaymentRecord(entry.record, locale));
  };

  const selectedTaxReviewEntry = selectedPaymentRecord
    ? taxReviewSummary.entries.find(
        (entry) => entry.record.id === selectedPaymentRecord.id,
      )
    : undefined;
  const selectedTaxReviewDecision =
    selectedTaxReviewEntry?.reviewStatus === "included"
      ? "include"
      : selectedTaxReviewEntry?.reviewStatus === "excluded"
        ? "exclude"
        : "unknown";

  const attachDocumentToRecord = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    const entry = taxReviewSummary.entries.find(
      (candidate) => candidate.record.id === attachmentRecordId,
    );
    if (!entry) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error(t("costs.tax_review.missing_document_linkage"));
    }

    const documents = attachDocumentToPaymentRecord(
      entry.record,
      document,
      t("costs.tax_review.missing_document_linkage"),
    );
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: entry.record.id,
      updates: { documents },
    });
    setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
    setAttachmentRecordId(null);
    await paymentRecordsQuery.refetch();
  };

  const createManualPaymentRecord = async (entry: ManualPaymentEntry) => {
    const refreshedRecords = entry.attachmentUploadId
      ? await paymentRecordsQuery.refetch()
      : null;
    const existing = entry.attachmentUploadId
      ? findPaymentRecordForUpload(
          refreshedRecords?.data ?? [],
          entry.attachmentUploadId,
        )
      : undefined;
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync(
      buildManualPaymentRecordPayload(entry, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        parseDate: parseDateText,
        today: todayIsoDate(),
      }),
    );
  };

  const createUploadedPaymentRecord = async (
    document: UploadedCostDocument,
  ) => {
    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = document.uploadId
      ? findPaymentRecordForUpload(
          refreshedRecords.data ?? [],
          document.uploadId,
        )
      : undefined;
    if (existing) return;

    await createPaymentRecordMutation.mutateAsync(
      buildUploadedPaymentRecordPayload(document, {
        missingDocumentLinkageMessage: t(
          "costs.tax_review.missing_document_linkage",
        ),
        uploadedDocumentLabel: (kind) => `Uploaded ${kind} document`,
        uploadedPhotoLabel: "Uploaded photo",
        uploadedFileLabel: "Uploaded file",
        today: todayIsoDate(),
      }),
    );
  };
  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout
      overlay={
        <>
          <PaymentEntryTypePicker
            visible={addTypePickerVisible}
            onClose={() => setAddTypePickerVisible(false)}
            onSelect={(kind) => {
              setSelectedAddKind(kind);
              setAddTypePickerVisible(false);
            }}
          />
          <CostDocumentUploadModal
            visible={selectedAddKind != null}
            mode="payment"
            initialPaymentKind={selectedAddKind ?? undefined}
            onClose={() => setSelectedAddKind(null)}
            onDocumentPicked={createUploadedPaymentRecord}
            onManualPaymentAdded={createManualPaymentRecord}
          />
          <CostDocumentUploadModal
            visible={attachmentRecordId !== null}
            mode="payment"
            documentOnly
            onClose={() => setAttachmentRecordId(null)}
            onDocumentPicked={attachDocumentToRecord}
            onManualPaymentAdded={async () => undefined}
          />
        </>
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab ? t("you.shared.back") : "Costs",
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab ? t("you.shared.back") : "Costs"}
          </Text>
        </Pressable>

        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          Tax Deductions
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 20,
          }}
        >
          U.S. federal guidance with tax years and official sources
        </Text>

        <View
          style={{
            ...(GLASS_CARD_STYLE as object),
            padding: 16,
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <ThiingsIcon name="hourglass" size={28} />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.text,
              lineHeight: 19,
            }}
          >
            Keep your receipts and treatment records together, then use the
            guidance below to review what may qualify and what to confirm before
            filing.
          </Text>
        </View>

        <View style={{ marginBottom: 16 }}>
          <TaxReviewSections
            summary={taxReviewSummary}
            calculation={taxReviewCalculation}
            calculationSnapshot={taxCalculationQuery.data}
            locale={locale}
            agiDraft={taxReviewAgiDraft}
            itemizationDraft={taxReviewItemizationDraft}
            inlineInputError={taxReviewInlineInputError}
            onAgiChange={handleTaxReviewAgiChange}
            onItemizationChange={handleTaxReviewItemizationChange}
            onAddExpense={() => setAddTypePickerVisible(true)}
            onOpenDetails={openPaymentDetails}
            infoSections={taxReviewInfoSections}
          />
        </View>

        <View style={{ gap: 16 }}>
          {TAX_BENEFITS.filter(
            (benefit) => benefit.id !== "medical-expense-deduction",
          ).map((benefit) =>
            benefit.id === "hsa-fsa" ? (
              <View key={benefit.id}>
                <SectionTitleRow
                  title="HSA / FSA"
                  icon={PiggyBank}
                  style={{ marginTop: 12 }}
                />
                <SpendingAccountCard
                  locale={locale}
                  onOpenChat={openSpendingAccountChat}
                />
              </View>
            ) : (
              <TaxBenefitCard key={benefit.id} benefit={benefit} />
            ),
          )}

          <View
            style={{
              flexDirection: "row",
              gap: 10,
              padding: 16,
            }}
          >
            <AlertCircle
              size={16}
              color={EMBER.colors.textFaint}
              strokeWidth={1.5}
              style={{ marginTop: 1 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 12,
                color: EMBER.colors.textFaint,
                lineHeight: 18,
              }}
            >
              This information is educational and not tax advice. Consult a
              qualified tax professional about your specific situation.
            </Text>
          </View>
        </View>
      </ScrollView>

      {selectedPaymentRecord ? (
        <PaymentRecordDetailModal
          record={selectedPaymentRecord}
          locale={locale}
          onClose={() => setSelectedPaymentRecord(null)}
          onAttachDocument={() =>
            setAttachmentRecordId(selectedPaymentRecord.id)
          }
          onSaveRecord={async (record) => {
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: record.id,
              updates: uiToPaymentRecordUpdates(record),
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxEligibility={selectedPaymentRecord.taxEligibility}
          onTaxEligibilityChange={async (taxEligibility) => {
            if (!selectedPaymentRecord) return;
            const updated = await updatePaymentRecordMutation.mutateAsync({
              paymentRecordId: selectedPaymentRecord.id,
              updates: { taxEligibility },
            });
            setSelectedPaymentRecord(apiToUiPaymentRecord(updated, locale));
            await paymentRecordsQuery.refetch();
          }}
          taxReviewDecision={selectedTaxReviewDecision}
          onTaxReviewDecision={async (decision) => {
            if (!selectedPaymentRecord) return;
            await saveTaxReview.mutateAsync({
              paymentRecordId: selectedPaymentRecord.id,
              decision,
            });
          }}
          onResetTaxReviewDecision={async () => {
            if (!selectedPaymentRecord) return;
            await deleteTaxReview.mutateAsync(selectedPaymentRecord.id);
          }}
        />
      ) : null}
    </GlassScreenLayout>
  );
}
